import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { Row, Col, Form, Button, Pagination, Modal, Table } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ButtonComponent } from "../../../components/elements";
import { FiEdit, FiTrash2, FiSearch, FiX, FiUser, FiPlus, FiRefreshCw, FiMail, FiCalendar, FiShield } from "react-icons/fi";
import Swal from 'sweetalert2';

export default function UserIndex() {
    const { t } = useContext(TranslatorContext);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0
    });

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Filter states
    const [filters, setFilters] = useState({
        name: searchParams.get('name') || "",
        email: searchParams.get('email') || "",
        status: searchParams.get('status') === "inactive" ? "inactive" : "active",
        per_page: parseInt(searchParams.get('per_page')) || 10,
        page: parseInt(searchParams.get('page')) || 1
    });

    const [applyingFilter, setApplyingFilter] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ show: false, userId: null, userName: "" });

    // Fetch users with filters and pagination
    const [statusUpdatingId, setStatusUpdatingId] = useState(null);

    const toggleUserStatus = async (user) => {
        const nextState = !user.is_active;

        const confirmed = await Swal.fire({
            title: nextState ? t('Activate this user?') : t('Deactivate this user?'),
            html: nextState
                ? `<strong>${user.name}</strong> ${t('will be able to sign in again.')}`
                : `<strong>${user.name}</strong> ${t('will be signed out and blocked from signing in.')}`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: nextState ? t('Yes, activate') : t('Yes, deactivate'),
            cancelButtonText: t('Cancel'),
            confirmButtonColor: nextState ? '#198754' : '#d33',
        });

        if (!confirmed.isConfirmed) return;

        setStatusUpdatingId(user.id);

        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL}/users/${user.id}/status`,
                { is_active: nextState },
                { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
            );

            if (response.data.success) {
                setUsers((prev) =>
                    prev.map((item) =>
                        item.id === user.id ? { ...item, is_active: nextState } : item
                    )
                );

                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: response.data.message,
                    showConfirmButton: false,
                    timer: 3000,
                });
            }
        } catch (error) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: error.response?.data?.message || t('Failed to update user status'),
                showConfirmButton: false,
                timer: 4000,
            });
        } finally {
            setStatusUpdatingId(null);
        }
    };

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");

            // Build query params
            const params = new URLSearchParams();
            if (filters.name) params.append('name', filters.name);
            if (filters.email) params.append('email', filters.email);
            params.append('status', filters.status);
            if (filters.per_page) params.append('per_page', filters.per_page);
            if (filters.page) params.append('page', filters.page);

            const url = `${process.env.REACT_APP_API_URL}/users${params.toString() ? `?${params.toString()}` : ''}`;

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                const usersData = res.data.users;
                setUsers(usersData.data || []);
                setPagination({
                    current_page: usersData.current_page || 1,
                    last_page: usersData.last_page || 1,
                    per_page: usersData.per_page || 10,
                    total: usersData.total || 0,
                    from: usersData.from || 0,
                    to: usersData.to || 0
                });
            }
        } catch (err) {
            console.error("Error fetching users:", err);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t("Failed to load users"),
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    }, [filters.name, filters.email, filters.status, filters.per_page, filters.page, t]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Update URL when filters change
    useEffect(() => {
        const params = new URLSearchParams();
        if (filters.name) params.append('name', filters.name);
        if (filters.email) params.append('email', filters.email);
        if (filters.status !== "active") params.append('status', filters.status);
        if (filters.per_page) params.append('per_page', filters.per_page);
        if (filters.page && filters.page !== 1) params.append('page', filters.page);
        setSearchParams(params);
    }, [filters, setSearchParams]);

    // Handle filter change
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value, page: 1 }));
    };

    // Apply filters
    const applyFilters = () => {
        setApplyingFilter(true);
        setFilters(prev => ({ ...prev, page: 1 }));
        setTimeout(() => setApplyingFilter(false), 500);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            name: "",
            email: "",
            status: "active",
            per_page: 10,
            page: 1
        });
    };

    // Handle per page change
    const handlePerPageChange = (e) => {
        const newPerPage = parseInt(e.target.value);
        setFilters(prev => ({ ...prev, per_page: newPerPage, page: 1 }));
    };

    // Handle page change - FIXED
    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page && page !== filters.page) {
            setFilters(prev => ({ ...prev, page: page }));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Handle delete
    const handleDelete = async () => {
        try {
            const token = localStorage.getItem("authToken");
            await axios.delete(`${process.env.REACT_APP_API_URL}/users/${deleteModal.userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: t("User deleted successfully"),
                showConfirmButton: false,
                timer: 2000
            });

            setDeleteModal({ show: false, userId: null, userName: "" });

            // Check if current page has no items after deletion
            if (users.length === 1 && pagination.current_page > 1) {
                setFilters(prev => ({ ...prev, page: pagination.current_page - 1 }));
            } else {
                fetchUsers();
            }
        } catch (err) {
            console.error("Error deleting user:", err);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: err.response?.data?.message || t("Failed to delete user"),
                showConfirmButton: false,
                timer: 2000
            });
            setDeleteModal({ show: false, userId: null, userName: "" });
        }
    };

    const confirmDelete = (userId, userName) => {
        setDeleteModal({ show: true, userId, userName });
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            applyFilters();
        }
    };

    const renderPaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.last_page, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.current_page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        return items;
    };

    if (loading && users.length === 0) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t("Loading...")}</span>
                    </div>
                    <p className="mt-3">{t("Loading users...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb p-3">
                            <h3 className="mc-breadcrumb-title mb-0">{t('User Management')}</h3>
                            <ul className="mc-breadcrumb-list mb-0">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('Users')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-2">
                            <h5 className="mb-0">{t('User List')}</h5>
                            <div className="d-flex gap-2">
                                {hasPermission('Users create') && (
                                    <ButtonComponent
                                        type="button"
                                        className="btn btn-primary d-inline-flex align-items-center gap-1"
                                        onClick={() => navigate("/user-create")}
                                        text={
                                            <>
                                                <FiPlus size={16} /> {t("Create New User")}
                                            </>
                                        }
                                    />
                                )}
                            </div>
                        </div>

                        {/* Filter Section */}
                        <div className="p-3 border-bottom bg-light">
                            <Row className="g-3 align-items-end">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t("Name")}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            size="sm"
                                            value={filters.name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder={t("Search by name...")}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t("Email")}</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            size="sm"
                                            value={filters.email}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder={t("Search by email...")}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t("Status")}</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            name="status"
                                            value={filters.status}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="active">{t("Active")}</option>
                                            <option value="inactive">{t("Inactive")}</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t("Per Page")}</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            value={filters.per_page}
                                            onChange={handlePerPageChange}
                                        >
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={applyFilters}
                                            disabled={applyingFilter}
                                            className="d-inline-flex align-items-center gap-1"
                                        >
                                            {applyingFilter ? (
                                                <>
                                                    <div className="spinner-border spinner-border-sm" role="status" />
                                                    <span>{t("Searching...")}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FiSearch size={14} /> {t("Search")}
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={resetFilters}
                                            className="d-inline-flex align-items-center gap-1"
                                        >
                                            <FiRefreshCw size={14} /> {t("Reset")}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Results Info */}
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-2">
                            <small className="text-muted">
                                {t("Showing")} {pagination.from} {t("to")} {pagination.to} {t("of")} {pagination.total} {t("users")}
                            </small>
                            {loading && (
                                <small>
                                    <div className="spinner-border spinner-border-sm text-primary me-1" role="status" />
                                    {t("Refreshing...")}
                                </small>
                            )}
                        </div>

                        {/* Table - Changed to Bootstrap Table with global classes */}
                        <div className="table-responsive-wrapper">
                            <Table responsive hover className="table mb-0">
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: '5%' }}>#</th>
                                        <th style={{ width: '25%' }}>{t('Name')}</th>
                                        <th style={{ width: '20%' }}>{t('Email')}</th>
                                        <th style={{ width: '20%' }}>{t('Role')}</th>
                                        <th style={{ width: '12%' }}>{t('Created At')}</th>
                                        <th className="text-center" style={{ width: '10%' }}>{t('Status')}</th>
                                        <th className="text-center" style={{ width: '13%' }}>{t('Action')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.length > 0 ? (
                                        users.map((user, index) => (
                                            <tr key={user.id}>
                                                <td className="text-center">
                                                    {(pagination.current_page - 1) * pagination.per_page + index + 1}
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div
                                                            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                                                            style={{ width: "35px", height: "35px", fontSize: "14px", fontWeight: "bold" }}
                                                        >
                                                            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                                        </div>
                                                        <div>
                                                            <strong className="text-primary">{user.name}</strong>
                                                            <div className="small text-muted">
                                                                <FiUser size={12} className="me-1" />
                                                                {user.role || 'user'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <FiMail className="text-muted" size={14} />
                                                        <a href={`mailto:${user.email}`} className="text-decoration-none">
                                                            {user.email}
                                                        </a>
                                                    </div>
                                                </td>
                                                <td>
                                                    {user.roles && user.roles.length > 0 ? (
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {user.roles.map((role, idx) => (
                                                                <span key={idx} className="badge bg-info d-inline-flex align-items-center gap-1">
                                                                    <FiShield size={10} />
                                                                    {role.name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="badge bg-secondary">{t("No role")}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <FiCalendar className="text-muted" size={14} />
                                                        <small>{new Date(user.created_at).toLocaleDateString()}</small>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <span
                                                        className={`badge ${user.is_active ? 'bg-success' : 'bg-secondary'}`}
                                                    >
                                                        {user.is_active ? t('Active') : t('Inactive')}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="mc-table-action justify-content-center gap-2">
                                                        {hasPermission('Users edit') && (
                                                            <Button
                                                                size="sm"
                                                                variant="primary"
                                                                className="border border-danger text-danger bg-transparent hover:bg-danger hover:text-white"
                                                                disabled={statusUpdatingId === user.id}
                                                                onClick={() => toggleUserStatus(user)}
                                                                title={
                                                                    user.is_active
                                                                        ? t('Deactivate user')
                                                                        : t('Activate user')
                                                                }
                                                            >
                                                                {user.is_active ? t('Deactivate') : t('Activate')}
                                                            </Button>
                                                        )}
                                                        {hasPermission('Users edit') && (
                                                            <ButtonComponent
                                                                type="button"
                                                                className="material-icons edit"
                                                                onClick={() => navigate(`/user-edit/${user.id}`)}
                                                                title={t("Edit")}
                                                            >
                                                                <FiEdit />
                                                            </ButtonComponent>
                                                        )}
                                                        {/* Delete button commented as per original */}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5">
                                                <div className="empty-state">
                                                    <FiUser size={48} className="text-muted mb-2" />
                                                    <p className="text-muted mb-0">{t("No users found")}</p>
                                                    {(filters.name || filters.email) && (
                                                        <Button
                                                            variant="link"
                                                            onClick={resetFilters}
                                                            className="mt-2"
                                                        >
                                                            {t("Clear filters")}
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {pagination.last_page > 1 && (
                            <div className="d-flex justify-content-between align-items-center p-3 border-top flex-wrap gap-2">
                                <div className="text-muted small">
                                    {t("Page")} {pagination.current_page} {t("of")} {pagination.last_page}
                                </div>
                                <Pagination className="mb-0">
                                    <Pagination.First
                                        onClick={() => handlePageChange(1)}
                                        disabled={pagination.current_page === 1}
                                    />
                                    <Pagination.Prev
                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                        disabled={pagination.current_page === 1}
                                    />

                                    {renderPaginationItems()}

                                    <Pagination.Next
                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    />
                                    <Pagination.Last
                                        onClick={() => handlePageChange(pagination.last_page)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Delete Confirmation Modal - Kept unchanged as requested */}
            <Modal show={deleteModal.show} onHide={() => setDeleteModal({ show: false, userId: null, userName: "" })} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t("Delete User")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t("Are you sure you want to delete the user")} <strong>{deleteModal.userName}</strong>?</p>
                    <p className="text-danger small">{t("This action cannot be undone.")}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDeleteModal({ show: false, userId: null, userName: "" })}>
                        {t("Cancel")}
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        {t("Delete")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}